import { useEffect, useMemo } from "react";
import { isProviderVisibleAtLocation } from "@/components/provider-select/provider-location-visibility";
import { type ProviderGroup, computeProviderGroups, reconcileProviderPreferences, requiresMultipleProviders } from "@/lib/self-schedule-provider-groups";
import { PublicProviderSummary } from "@/types/provider";
import { FrontPage } from "@/types/self-schedule";
import { PublicServiceDetail } from "@/types/service";
import type { SelfScheduleStore } from "@/stores/self-schedule-store";
import { FIRST_AVAILABLE_PROVIDER_ID, useSelfScheduleStore } from "@/stores/self-schedule-store";
interface UseProviderAssignmentArgs {
  frontPageData: FrontPage | null | undefined;
  isFrontPageLoading: boolean;
  /** One PublicServiceDetail per instance — duplicates of the same service repeat. */
  selectedServices: PublicServiceDetail[] | undefined;
  selectedServiceInstances: SelfScheduleStore["selectedServiceInstances"];
  selectedAvailabilityAssignments: SelfScheduleStore["selectedAvailabilityAssignments"];
  serviceInstanceProviderIds: Record<string, string>;
  selectedProviderId: string | null;
  selectedLocationId: string | null;
  providerPreferenceMode?: "grouped" | "per-occurrence";
  confirmedAppointmentReview: boolean;
  createdAppointmentId: string | null;
}
export interface ProviderAssignment {
  /** True when no selected service lets the patient choose a provider. */
  skipAppointmentReview: boolean;
  providerGroups: ProviderGroup[];
  isMultiProviderSelection: boolean;
  usesPerOccurrenceProviderSelection: boolean;
  appointmentReviewComplete: boolean;
  selectedProvider: PublicProviderSummary | undefined;
  /** Concrete providers aligned with selectedAvailabilityAssignments. */
  selectedAvailabilityProviders: (PublicProviderSummary | undefined)[];
  selectedAvailabilityProvider: PublicProviderSummary | undefined;
  availableProviders: PublicProviderSummary[];
}

/**
 * Who performs each selected service.
 *
 * Owns the whole provider-assignment policy — partitioning services into
 * groups, reconciling persisted preferences against the latest backend
 * eligibility, and deciding when the appointment review is satisfied — so the
 * booking flow hook only consumes the answers.
 */
export const useProviderAssignment = ({
  frontPageData,
  isFrontPageLoading,
  selectedServices,
  selectedServiceInstances,
  selectedAvailabilityAssignments,
  serviceInstanceProviderIds,
  selectedProviderId,
  selectedLocationId,
  confirmedAppointmentReview,
  providerPreferenceMode = "grouped",
  createdAppointmentId
}: UseProviderAssignmentArgs): ProviderAssignment => {
  const usesPerOccurrenceProviderSelection = providerPreferenceMode === "per-occurrence";
  const addonProviderIds = useSelfScheduleStore(state => state.addonProviderIds);
  // Provider choice is a per-service setting, so one auto-assign service must not
  // suppress the review for the others. Skip it only when nothing is choosable.
  const skipAppointmentReview = useMemo(() => {
    if (!selectedServices?.length) return false;
    return selectedServices.every(service => !service.allowIndividualProviderBooking);
  }, [selectedServices]);

  // Multi-provider self-scheduling: partition the selected services into groups
  // that each need their own provider. If a single provider covers every selected
  // service, the flow stays single-provider.
  const providerGroups: ProviderGroup[] = useMemo(() => {
    if (selectedServiceInstances.length === 0) return [];
    return computeProviderGroups(selectedServiceInstances, frontPageData?.services ?? [], frontPageData?.providers ?? [], selectedLocationId, providerPreferenceMode);
  }, [selectedServiceInstances, frontPageData?.services, frontPageData?.providers, selectedLocationId, providerPreferenceMode]);

  // True when the selection can't be served by a single provider, so the
  // appointment review step must collect one provider per group.
  const isMultiProviderSelection = useMemo(() => usesPerOccurrenceProviderSelection ? requiresMultipleProviders(computeProviderGroups(selectedServiceInstances, frontPageData?.services ?? [], frontPageData?.providers ?? [], selectedLocationId)) : requiresMultipleProviders(providerGroups), [providerGroups, usesPerOccurrenceProviderSelection, selectedServiceInstances, frontPageData, selectedLocationId]);

  // Persisted provider preferences are only hints. Reconcile them once the
  // latest backend provider/service lists are available, and invalidate review
  // confirmation whenever a stale truthy ID is removed. The chosen slot was
  // found for the dropped provider, so it goes too — otherwise reconfirming the
  // review can skip the time picker and rebook the stale assignments.
  useEffect(() => {
    if (isFrontPageLoading || !frontPageData || selectedServiceInstances.length === 0) {
      return;
    }
    const reconciled = reconcileProviderPreferences(providerGroups, selectedProviderId, serviceInstanceProviderIds);
    if (usesPerOccurrenceProviderSelection) {
      for (const instance of selectedServiceInstances) {
        const service_1 = frontPageData.services.find(service_0 => service_0.id === instance.id);
        const id = reconciled.serviceInstanceProviderIds[instance.instanceId];
        if (id && id !== FIRST_AVAILABLE_PROVIDER_ID && !service_1?.allowIndividualProviderBooking) {
          reconciled.serviceInstanceProviderIds[instance.instanceId] = FIRST_AVAILABLE_PROVIDER_ID;
          reconciled.droppedSelection = true;
        }
      }
    }
    // Public availability doesn't check online visibility, so drop add-on
    // providers hidden at the chosen location the way the picker would.
    let droppedAddonProvider = false;
    const reconciledAddonProviderIds = Object.fromEntries(Object.entries(addonProviderIds).map(([key, providerId]) => {
      if (!selectedLocationId || providerId === FIRST_AVAILABLE_PROVIDER_ID) return [key, providerId];
      const provider = frontPageData.providers.find(({
        id: id_0
      }) => id_0 === providerId);
      if (provider && isProviderVisibleAtLocation(provider.visibleLocationIds, selectedLocationId)) return [key, providerId];
      droppedAddonProvider = true;
      return [key, FIRST_AVAILABLE_PROVIDER_ID];
    }));
    if (!reconciled.droppedSelection && !droppedAddonProvider) return;
    useSelfScheduleStore.setState({
      addonProviderIds: reconciledAddonProviderIds,
      selectedProviderId: reconciled.selectedProviderId,
      serviceInstanceProviderIds: reconciled.serviceInstanceProviderIds,
      serviceInstanceProviderOrigins: Object.fromEntries(Object.entries(useSelfScheduleStore.getState().serviceInstanceProviderOrigins).filter(([id_1]) => id_1 in reconciled.serviceInstanceProviderIds)),
      confirmedAppointmentReview: false,
      // An already-reserved appointment keeps its slot — that time is held on
      // the server and the patient only owes payment.
      ...(createdAppointmentId ? {} : {
        selectedDateTime: null,
        selectedAvailabilityAssignments: []
      })
    });
  }, [addonProviderIds, createdAppointmentId, frontPageData, isFrontPageLoading, selectedLocationId, providerGroups, selectedProviderId, selectedServiceInstances.length, serviceInstanceProviderIds, usesPerOccurrenceProviderSelection, selectedServiceInstances]);

  // Whether the appointment review step is satisfied: single-provider mode needs one
  // chosen provider (or a skipped step); multi-provider mode needs every
  // service instance in every group assigned a provider.
  const appointmentReviewComplete = useMemo(() => {
    if (skipAppointmentReview) return true;
    if (!confirmedAppointmentReview) return false;
    if (providerGroups.some(group => group.eligibleProviderIds.length === 0)) {
      return false;
    }
    if (usesPerOccurrenceProviderSelection || isMultiProviderSelection) {
      return providerGroups.every(group_0 => group_0.instances.every(instance_0 => {
        const assignedProviderId = serviceInstanceProviderIds[instance_0.instanceId];
        return assignedProviderId === FIRST_AVAILABLE_PROVIDER_ID || group_0.eligibleProviderIds.includes(assignedProviderId);
      }));
    }
    if (selectedProviderId === null) return false;
    return selectedProviderId === FIRST_AVAILABLE_PROVIDER_ID || providerGroups[0]?.eligibleProviderIds.includes(selectedProviderId) === true;
  }, [skipAppointmentReview, usesPerOccurrenceProviderSelection, isMultiProviderSelection, providerGroups, serviceInstanceProviderIds, selectedProviderId, confirmedAppointmentReview]);
  const selectedProvider = useMemo(() => {
    return frontPageData?.providers?.find(provider_0 => provider_0.id === selectedProviderId);
  }, [frontPageData?.providers, selectedProviderId]);
  const selectedAvailabilityProviders = useMemo(() => selectedAvailabilityAssignments.map(assignment => frontPageData?.providers?.find(provider_1 => provider_1.id === assignment.providerId)), [frontPageData?.providers, selectedAvailabilityAssignments]);
  const availableProviders = useMemo(() => {
    if (!frontPageData?.providers) return [];
    if (selectedServiceInstances.length === 0) {
      return frontPageData.providers;
    }

    // Use the same backend-derived eligibility as review completion. In
    // particular, an empty service provider list means no provider — never
    // "every provider" — so a lone stale service reaches recovery UI.
    return providerGroups.length === 1 ? providerGroups[0].eligibleProviders : [];
  }, [frontPageData?.providers, providerGroups, selectedServiceInstances.length]);
  return {
    skipAppointmentReview,
    providerGroups,
    isMultiProviderSelection,
    usesPerOccurrenceProviderSelection,
    appointmentReviewComplete,
    selectedProvider,
    selectedAvailabilityProviders,
    selectedAvailabilityProvider: selectedAvailabilityProviders[0],
    availableProviders
  };
};